/** Default datasets for the data_sandbox block (AI Essentials L14). */

export const DEFAULT_DATA_SANDBOX_DATASETS = [
  {
    id: "cafe",
    label: "☕ Café sales (2025)",
    info: "Chai Corner's monthly sales. Revenue is in rupees.",
    default_metric: "revenue",
    columns: [
      { id: "month", label: "Month" },
      { id: "chai", label: "Chai cups", numeric: true },
      { id: "snacks", label: "Snacks sold", numeric: true },
      { id: "revenue", label: "Revenue (Rs)", numeric: true },
    ],
    rows: [
      { month: "Jan", chai: 4200, snacks: 1800, revenue: 1260000 },
      { month: "Feb", chai: 4000, snacks: 1750, revenue: 1195000 },
      { month: "Mar", chai: 3100, snacks: 1200, revenue: 905000 },
      { month: "Apr", chai: 4600, snacks: 2100, revenue: 1415000 },
      { month: "May", chai: 4500, snacks: 2000, revenue: 1380000 },
      { month: "Jun", chai: 3900, snacks: 1600, revenue: 1170000 },
      { month: "Jul", chai: 3700, snacks: 1500, revenue: 1110000 },
      { month: "Aug", chai: 3800, snacks: 1550, revenue: 1135000 },
      { month: "Sep", chai: 4700, snacks: 2200, revenue: 1450000 },
      { month: "Oct", chai: 5100, snacks: 2400, revenue: 1575000 },
      { month: "Nov", chai: 5400, snacks: 2600, revenue: 1690000 },
      { month: "Dec", chai: 5900, snacks: 2900, revenue: 1850000 },
    ],
    judgments: [
      {
        id: "c1",
        question: "Which month had the highest revenue?",
        ai_claim: "December, with Rs 1,850,000.",
        ai_correct: true,
        truth: "Dec: Rs 1,850,000. The AI is right.",
      },
      {
        id: "c2",
        question: "What was the average monthly revenue?",
        ai_claim: "About Rs 1,320,000 per month.",
        ai_correct: false,
        truth:
          "The average is Rs 1,344,583. The AI gave the median (the middle value), not the average. A mixed-up measure.",
      },
      {
        id: "c3",
        question: "What will revenue be in January 2026?",
        ai_claim:
          "January 2026 revenue will be exactly Rs 1,950,000, continuing the upward trend.",
        ai_correct: false,
        truth:
          "Nobody can know exactly. Revenue did rise from September to December, but last January (Rs 1,260,000) was far lower than December (Rs 1,850,000), so there may be a seasonal dip. A sensible prediction gives a range and states its assumptions. An exact number is false confidence.",
      },
    ],
  },
  {
    id: "school",
    label: "🎓 Grade 10 maths results",
    info: "16 students, anonymised. Homework is the % of homework completed; Score is the final test out of 100.",
    default_metric: "score",
    columns: [
      { id: "student", label: "Student" },
      { id: "class", label: "Class" },
      { id: "homework", label: "Homework (%)", numeric: true },
      { id: "score", label: "Score", numeric: true },
    ],
    rows: [
      { student: "S01", class: "A", homework: 90, score: 82 },
      { student: "S02", class: "A", homework: 75, score: 68 },
      { student: "S03", class: "A", homework: 40, score: 45 },
      { student: "S04", class: "A", homework: 85, score: 77 },
      { student: "S05", class: "A", homework: 60, score: 55 },
      { student: "S06", class: "A", homework: 95, score: 91 },
      { student: "S07", class: "A", homework: 30, score: 38 },
      { student: "S08", class: "A", homework: 70, score: 64 },
      { student: "S09", class: "B", homework: 80, score: 71 },
      { student: "S10", class: "B", homework: 55, score: 49 },
      { student: "S11", class: "B", homework: 65, score: 58 },
      { student: "S12", class: "B", homework: 35, score: 42 },
      { student: "S13", class: "B", homework: 90, score: 85 },
      { student: "S14", class: "B", homework: 45, score: 47 },
      { student: "S15", class: "B", homework: 75, score: 66 },
      { student: "S16", class: "B", homework: 50, score: 52 },
    ],
    judgments: [
      {
        id: "s1",
        question: "Which class scored higher on average?",
        ai_claim: "Class A, averaging 65 compared with 58.75 for Class B.",
        ai_correct: true,
        truth: "Class A: 65, Class B: 58.75. The AI is right.",
      },
      {
        id: "s2",
        question: "How many students scored below 50?",
        ai_claim: "3 students scored below 50.",
        ai_correct: false,
        truth:
          "5 students: S03, S07, S10, S12, S14. The AI miscounted. Counting is surprisingly easy for AI to get wrong.",
      },
      {
        id: "s3",
        question: "Does doing more homework lead to higher scores?",
        ai_claim:
          "Yes. Doing more homework causes higher scores, so making homework compulsory will raise results.",
        ai_correct: false,
        truth:
          "There's a very strong link (correlation 0.98), but that doesn't prove homework causes the scores. Motivated students may do both. And 16 students is a small sample. Correlation isn't causation.",
      },
    ],
  },
  {
    id: "clinic",
    label: "🏥 Clinic appointments (one week)",
    info: "A small clinic's bookings for one week. Wait is the average wait in minutes.",
    default_metric: "wait",
    columns: [
      { id: "day", label: "Day" },
      { id: "booked", label: "Booked", numeric: true },
      { id: "noshows", label: "No-shows", numeric: true },
      { id: "wait", label: "Avg wait (min)", numeric: true },
    ],
    rows: [
      { day: "Mon", booked: 62, noshows: 11, wait: 48 },
      { day: "Tue", booked: 48, noshows: 5, wait: 22 },
      { day: "Wed", booked: 50, noshows: 6, wait: 25 },
      { day: "Thu", booked: 47, noshows: 4, wait: 20 },
      { day: "Fri", booked: 40, noshows: 9, wait: 30 },
      { day: "Sat", booked: 55, noshows: 8, wait: 35 },
    ],
    judgments: [
      {
        id: "cl1",
        question: "Which day has the most no-shows?",
        ai_claim: "Monday, with 11 no-shows.",
        ai_correct: true,
        truth: "Mon, with 11. The AI is right.",
      },
      {
        id: "cl2",
        question: "What's the no-show rate for the week?",
        ai_claim: "The no-show rate is about 7 per day, or 7%.",
        ai_correct: false,
        truth:
          "43 no-shows out of 302 bookings = 14.2%. The AI confused an average count per day with a rate.",
      },
      {
        id: "cl3",
        question: "Why are Monday waits so long?",
        ai_claim:
          "Monday waits are long because of staff shortages at the start of the week.",
        ai_correct: false,
        truth:
          "The data shows Monday has the most bookings (62) and the longest wait (48 minutes). It says nothing about staff. The AI invented a cause. More bookings is one likely factor worth investigating.",
      },
    ],
  },
];
